// Web Crypto wrapper. Everything is AES-GCM 256-bit with a key derived from the
// user's PIN via PBKDF2-HMAC-SHA256.
//
// Design notes:
//   - Salt is generated once on first PIN setup and stored alongside the
//     verifier in the auth config store. Salts are not secret.
//   - Iteration count is OWASP 2023's recommendation for PBKDF2-HMAC-SHA256.
//     This intentionally costs ~200–500ms on a modern phone — enough to slow
//     brute force, fast enough not to be annoying for one PIN entry per unlock.
//   - The derived key is held only in memory by the session module — never
//     persisted. Forgetting the PIN means the data cannot be recovered.

const ALG = 'AES-GCM';
const KEY_BITS = 256;
const IV_BYTES = 12;
const SALT_BYTES = 16;
const PBKDF2_ITERATIONS = 600_000;
const VERIFY_PLAINTEXT = 'maxhealth-pin-verifier-v1';

function randomBytes(n) {
  return crypto.getRandomValues(new Uint8Array(n));
}

export function generateSalt() {
  return randomBytes(SALT_BYTES);
}

/**
 * Derive an AES-GCM key from a PIN and salt.
 * The result is a non-extractable CryptoKey usable only for encrypt/decrypt.
 */
export async function deriveKey(pin, salt) {
  const pinBytes = new TextEncoder().encode(pin);
  const baseKey = await crypto.subtle.importKey(
    'raw',
    pinBytes,
    'PBKDF2',
    false,
    ['deriveKey']
  );
  return await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    baseKey,
    { name: ALG, length: KEY_BITS },
    false, // non-extractable
    ['encrypt', 'decrypt']
  );
}

/**
 * Create a "verifier" — a known plaintext encrypted with the derived key.
 * Stored at PIN setup time. On unlock attempts we decrypt this with the
 * candidate key and check the plaintext matches — wrong PIN gives decryption
 * failure, never revealing whether the key is right via timing.
 */
export async function makeVerifier(key) {
  return await encryptString(key, VERIFY_PLAINTEXT);
}

export async function checkVerifier(key, verifier) {
  try {
    const plain = await decryptString(key, verifier);
    return plain === VERIFY_PLAINTEXT;
  } catch {
    return false; // AES-GCM auth failure on wrong key
  }
}

// ---- Encryption primitives ----

/**
 * Encrypt a Blob. Returns { iv, cipher } where cipher is a Blob containing the
 * raw ciphertext bytes. Caller stores both — IV is not secret, but it must be
 * unique per encryption with the same key.
 */
export async function encryptBlob(key, blob) {
  const iv = randomBytes(IV_BYTES);
  const plaintext = await blob.arrayBuffer();
  const cipherBuf = await crypto.subtle.encrypt({ name: ALG, iv }, key, plaintext);
  return { iv, cipher: new Blob([cipherBuf]) };
}

/**
 * Decrypt a Blob previously produced by encryptBlob.
 * Returns a Blob preserving the requested mimeType (default image/jpeg).
 */
export async function decryptBlob(key, { iv, cipher }, mimeType = 'image/jpeg') {
  const ivBytes = iv instanceof Uint8Array ? iv : new Uint8Array(iv);
  const cipherBuf = await cipher.arrayBuffer();
  const plainBuf = await crypto.subtle.decrypt({ name: ALG, iv: ivBytes }, key, cipherBuf);
  return new Blob([plainBuf], { type: mimeType });
}

/** Encrypt a JS value via JSON.stringify. Returns { iv, cipher } as bytes. */
export async function encryptJSON(key, value) {
  const iv = randomBytes(IV_BYTES);
  const plaintext = new TextEncoder().encode(JSON.stringify(value));
  const cipherBuf = await crypto.subtle.encrypt({ name: ALG, iv }, key, plaintext);
  return { iv, cipher: new Uint8Array(cipherBuf) };
}

/** Decrypt a JSON payload. */
export async function decryptJSON(key, { iv, cipher }) {
  const ivBytes = iv instanceof Uint8Array ? iv : new Uint8Array(iv);
  const cipherBytes = cipher instanceof Uint8Array ? cipher : new Uint8Array(cipher);
  const plainBuf = await crypto.subtle.decrypt({ name: ALG, iv: ivBytes }, key, cipherBytes);
  return JSON.parse(new TextDecoder().decode(plainBuf));
}

/** Internal: encrypt a string. Returns { iv, cipher } as Uint8Array. */
async function encryptString(key, str) {
  const iv = randomBytes(IV_BYTES);
  const cipherBuf = await crypto.subtle.encrypt(
    { name: ALG, iv },
    key,
    new TextEncoder().encode(str)
  );
  return { iv, cipher: new Uint8Array(cipherBuf) };
}

async function decryptString(key, { iv, cipher }) {
  const ivBytes = iv instanceof Uint8Array ? iv : new Uint8Array(iv);
  const cipherBytes = cipher instanceof Uint8Array ? cipher : new Uint8Array(cipher);
  const plainBuf = await crypto.subtle.decrypt({ name: ALG, iv: ivBytes }, key, cipherBytes);
  return new TextDecoder().decode(plainBuf);
}
